import { useState, useEffect, useCallback } from "react";
import {
  Users, FileText, Newspaper, Scale, Briefcase, IndianRupee,
  Plus, Trash2, MessageSquare, Loader2, Upload, Image as ImageIcon, Eye,
} from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "@/components/ui/sonner";

const inputCls =
  "w-full bg-surface border border-hairline rounded-xl px-4 py-3 text-sm outline-none focus:border-gold/40 text-ink placeholder:text-inkfaint";

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="glass-card rounded-2xl p-6" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <Icon className="w-6 h-6 text-gold" />
      <div className="font-serif text-4xl text-ink mt-4">{value}</div>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-inkfaint mt-2">{label}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState({});
  const [articles, setArticles] = useState([]);
  const [news, setNews] = useState([]);
  const [legal, setLegal] = useState([]);
  const [vacancies, setVacancies] = useState([]);
  const [applications, setApplications] = useState([]);
  const [users, setUsers] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [pyqs, setPyqs] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    const [s, a, n, l, v, ap, u, vf, pq] = await Promise.all([
      api.get("/admin/stats"), api.get("/articles"), api.get("/news"),
      api.get("/legal"), api.get("/vacancies"), api.get("/admin/applications"), api.get("/admin/users"),
      api.get("/admin/verifications"), api.get("/admin/pyq"),
    ]);
    setStats(s.data); setArticles(a.data); setNews(n.data); setLegal(l.data);
    setVacancies(v.data); setApplications(ap.data); setUsers(u.data); setVerifications(vf.data); setPyqs(pq.data);
  }, []);

  useEffect(() => { loadAll().catch(() => toast.error("Failed to load admin data.")); }, [loadAll]);

  // forms
  const [art, setArt] = useState({ title: "", category: "Legal Insight", excerpt: "", content: "", image_url: "", pdf_url: "", author: "JusticeVerse Editorial", read_time: "5 MIN READ", status: "published" });
  const [nw, setNw] = useState({ title: "", category: "Supreme Court", summary: "", content: "", image_url: "", source: "JusticeVerse Legal Reporter" });
  const [leg, setLeg] = useState({ title: "", doc_type: "case_law", court: "Supreme Court", category: "Supreme Court", citation: "", date: "", summary: "", source: "", link_url: "", attachment_url: "", attachment_name: "" });
  const [vac, setVac] = useState({ title: "", organization: "", type: "internship", location: "", stipend: "", description: "", tags: "", link_url: "", attachment_url: "", attachment_name: "" });
  const [pyq, setPyq] = useState({ exam: "Gujarat Judiciary", subject: "", year: "", qtype: "mcq", question: "", optA: "", optB: "", optC: "", optD: "", answer: "", explanation: "" });
  const [gen, setGen] = useState({ exam: "Gujarat Judiciary", subject: "", year: "", qtype: "mcq", count: 5 });
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(null);

  const uploadPdf = async (file, target) => {
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please upload a PDF file.");
      return;
    }
    if (file.size > 10_000_000) {
      toast.error("PDF too large (max 10 MB).");
      return;
    }
    setUploading(target);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      if (target === "vac") setVac((p) => ({ ...p, attachment_url: data.url, attachment_name: data.filename }));
      else setLeg((p) => ({ ...p, attachment_url: data.url, attachment_name: data.filename }));
      toast.success("PDF attached.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(null);
    }
  };

  const uploadImage = async (file, target) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file.");
      return;
    }
    if (file.size > 10_000_000) {
      toast.error("Image too large (max 10 MB).");
      return;
    }
    setUploading(target);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      if (target === "art-img") setArt((p) => ({ ...p, image_url: data.url }));
      else if (target === "news-img") setNw((p) => ({ ...p, image_url: data.url }));
      toast.success("Image attached.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(null);
    }
  };

  const uploadArtPdf = async (file) => {
    if (!file) return;
    const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) { toast.error("Please upload a PDF file."); return; }
    if (file.size > 10_000_000) { toast.error("PDF too large (max 10 MB)."); return; }
    setUploading("art-pdf");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setArt((p) => ({ ...p, pdf_url: data.url, pdf_name: data.filename }));
      toast.success("PDF attached.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(null);
    }
  };

  const run = async (fn) => {
    setSaving(true);
    try { await fn(); await loadAll(); toast.success("Saved."); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
    finally { setSaving(false); }
  };

  const addArticle = () => run(async () => { await api.post("/admin/articles", art); setArt({ ...art, title: "", excerpt: "", content: "", image_url: "", pdf_url: "", pdf_name: "" }); });
  const addNews = () => run(async () => { await api.post("/admin/news", nw); setNw({ ...nw, title: "", summary: "", content: "", image_url: "" }); });
  const addLegal = () => run(async () => { await api.post("/admin/legal", leg); setLeg({ ...leg, title: "", citation: "", summary: "", link_url: "", attachment_url: "", attachment_name: "" }); });
  const addVac = () => run(async () => {
    await api.post("/admin/vacancies", { ...vac, tags: vac.tags.split(",").map((t) => t.trim()).filter(Boolean) });
    setVac({ ...vac, title: "", organization: "", description: "", link_url: "", attachment_url: "", attachment_name: "" });
  });

  const del = (path, id) => run(async () => { await api.delete(`/admin/${path}/${id}`); });

  const addPyq = () => run(async () => {
    const options = pyq.qtype === "mcq" ? [`A. ${pyq.optA}`, `B. ${pyq.optB}`, `C. ${pyq.optC}`, `D. ${pyq.optD}`] : [];
    await api.post("/admin/pyq", {
      exam: pyq.exam, subject: pyq.subject, year: pyq.year, qtype: pyq.qtype,
      question: pyq.question, options, answer: pyq.answer, explanation: pyq.explanation,
    });
    setPyq({ ...pyq, question: "", optA: "", optB: "", optC: "", optD: "", answer: "", explanation: "" });
  });

  const genPyq = async () => {
    if (!gen.subject.trim()) { toast.error("Enter a subject to generate questions."); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/pyq/generate", { ...gen, count: Number(gen.count) });
      toast.success(`AI generated ${data.inserted} questions.`);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12" data-testid="admin-dashboard-page">
      <span className="font-mono text-xs uppercase tracking-[0.25em] text-gold">Owner Console</span>
      <h1 className="font-serif text-4xl sm:text-5xl font-light text-ink mt-3">Owner Dashboard</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
        <StatCard icon={Eye} label="Visitors" value={(stats.visitors ?? 0).toLocaleString("en-IN")} />
        <StatCard icon={Users} label="Users" value={stats.users ?? "—"} />
        <StatCard icon={Scale} label="Judgments" value={stats.judgments ?? "—"} />
        <StatCard icon={FileText} label="Articles" value={stats.articles ?? "—"} />
        <StatCard icon={MessageSquare} label="AI Queries" value={(stats.ai_queries ?? stats.chat_messages ?? 0).toLocaleString("en-IN")} />
        <StatCard icon={Newspaper} label="News" value={stats.news ?? "—"} />
        <StatCard icon={Briefcase} label="Vacancies" value={stats.vacancies ?? "—"} />
        <StatCard icon={Briefcase} label="Applications" value={stats.applications ?? "—"} />
        <StatCard icon={IndianRupee} label="Revenue" value={`₹${(stats.revenue ?? 0).toLocaleString("en-IN")}`} />
      </div>

      <Tabs defaultValue="articles" className="mt-12">
        <TabsList className="bg-surface border border-hairline rounded-full p-1 flex flex-wrap h-auto gap-1">
          {["articles", "news", "judgments", "vacancies", "pyqs", "applications", "verifications", "users"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`admin-tab-${t}`} className="rounded-full px-5 py-2 text-xs font-mono uppercase tracking-[0.15em] data-[state=active]:bg-btn data-[state=active]:text-btnink text-inkmuted">
              {t}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* ARTICLES */}
        <TabsContent value="articles" className="mt-8 space-y-6">
          <div className="glass-card rounded-2xl p-7 space-y-4" data-testid="article-form">
            <h3 className="font-serif text-2xl text-ink flex items-center gap-2"><Plus className="w-5 h-5 text-gold" /> New Article</h3>
            <input className={inputCls} placeholder="Title" value={art.title} onChange={(e) => setArt({ ...art, title: e.target.value })} data-testid="art-title" />
            <div className="grid sm:grid-cols-3 gap-4">
              <input className={inputCls} placeholder="Category" value={art.category} onChange={(e) => setArt({ ...art, category: e.target.value })} />
              <input className={inputCls} placeholder="Author" value={art.author} onChange={(e) => setArt({ ...art, author: e.target.value })} />
              <input className={inputCls} placeholder="Read time" value={art.read_time} onChange={(e) => setArt({ ...art, read_time: e.target.value })} />
            </div>
            <textarea className={inputCls} rows={2} placeholder="Excerpt" value={art.excerpt} onChange={(e) => setArt({ ...art, excerpt: e.target.value })} />
            <textarea className={inputCls} rows={4} placeholder="Full content" value={art.content} onChange={(e) => setArt({ ...art, content: e.target.value })} />
            <div className="flex items-center gap-4 flex-wrap">
              <label className="rounded-full border border-hairline text-ink hover:bg-surface2 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider cursor-pointer flex items-center gap-2" data-testid="art-image-label">
                {uploading === "art-img" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
                {art.image_url ? "Replace Image" : "Add Image"}
                <input type="file" accept="image/*" className="hidden" data-testid="art-image-input" onChange={(e) => uploadImage(e.target.files?.[0], "art-img")} />
              </label>
              {art.image_url && (
                <span className="flex items-center gap-2">
                  <img src={fileUrl(art.image_url)} alt="" className="w-14 h-14 rounded-lg object-cover border border-hairline" />
                  <button type="button" onClick={() => setArt({ ...art, image_url: "" })} className="text-inkfaint hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                </span>
              )}
              <label className="rounded-full border border-hairline text-ink hover:bg-surface2 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider cursor-pointer flex items-center gap-2" data-testid="art-pdf-label">
                {uploading === "art-pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                {art.pdf_url ? "Replace PDF" : "Add PDF"}
                <input type="file" accept="application/pdf,.pdf" className="hidden" data-testid="art-pdf-input" onChange={(e) => uploadArtPdf(e.target.files?.[0])} />
              </label>
              {art.pdf_url && (
                <span className="flex items-center gap-2 text-xs text-gold" data-testid="art-pdf-chip">
                  <FileText className="w-4 h-4" /> {art.pdf_name || "PDF attached"}
                  <button type="button" onClick={() => setArt({ ...art, pdf_url: "", pdf_name: "" })} className="text-inkfaint hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                </span>
              )}
            </div>
            {art.pdf_url && (
              <p className="text-[11px] text-inkfaint -mt-1">The PDF will be displayed on the site exactly as-is inside the article. Excerpt is used for the card preview.</p>
            )}
            <button onClick={addArticle} disabled={saving || !art.title} className="rounded-full bg-btn text-btnink px-6 py-2.5 text-xs font-semibold uppercase tracking-wider disabled:opacity-50 flex items-center gap-2" data-testid="add-article-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Publish Article
            </button>
          </div>
          {articles.map((a) => (
            <div key={a.id} className="glass-card rounded-xl p-5 flex items-center justify-between" data-testid="admin-article-row">
              <div className="flex items-center gap-4 min-w-0">
                {a.image_url && <img src={fileUrl(a.image_url)} alt="" className="w-12 h-12 rounded-lg object-cover border border-hairline shrink-0" />}
                <div className="min-w-0"><div className="text-ink truncate">{a.title}</div><div className="font-mono text-[11px] text-inkfaint mt-1">{a.category}</div></div>
              </div>
              <button onClick={() => del("articles", a.id)} className="text-inkfaint hover:text-red-400 shrink-0 ml-3"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </TabsContent>

        {/* NEWS */}
        <TabsContent value="news" className="mt-8 space-y-6">
          <div className="glass-card rounded-2xl p-7 space-y-4">
            <h3 className="font-serif text-2xl text-ink flex items-center gap-2"><Plus className="w-5 h-5 text-gold" /> New News Item</h3>
            <input className={inputCls} placeholder="Title" value={nw.title} onChange={(e) => setNw({ ...nw, title: e.target.value })} data-testid="news-title" />
            <div className="grid sm:grid-cols-2 gap-4">
              <select className={inputCls} value={nw.category} onChange={(e) => setNw({ ...nw, category: e.target.value })} data-testid="news-category">
                <option>Supreme Court</option><option>High Court</option><option>Articles</option><option>News Update</option>
              </select>
              <input className={inputCls} placeholder="Source" value={nw.source} onChange={(e) => setNw({ ...nw, source: e.target.value })} />
            </div>
            <textarea className={inputCls} rows={2} placeholder="Headline summary (short)" value={nw.summary} onChange={(e) => setNw({ ...nw, summary: e.target.value })} />
            <textarea className={inputCls} rows={4} placeholder="Full news text" value={nw.content} onChange={(e) => setNw({ ...nw, content: e.target.value })} data-testid="news-content" />
            <div className="flex items-center gap-4 flex-wrap">
              <label className="rounded-full border border-hairline text-ink hover:bg-surface2 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider cursor-pointer flex items-center gap-2" data-testid="news-image-label">
                {uploading === "news-img" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
                {nw.image_url ? "Replace Image" : "Add Image"}
                <input type="file" accept="image/*" className="hidden" data-testid="news-image-input" onChange={(e) => uploadImage(e.target.files?.[0], "news-img")} />
              </label>
              {nw.image_url && (
                <span className="flex items-center gap-2">
                  <img src={fileUrl(nw.image_url)} alt="" className="w-14 h-14 rounded-lg object-cover border border-hairline" />
                  <button type="button" onClick={() => setNw({ ...nw, image_url: "" })} className="text-inkfaint hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                </span>
              )}
            </div>
            <button onClick={addNews} disabled={saving || !nw.title} className="rounded-full bg-btn text-btnink px-6 py-2.5 text-xs font-semibold uppercase tracking-wider disabled:opacity-50" data-testid="add-news-btn">Publish News</button>
          </div>
          {news.map((n) => (
            <div key={n.id} className="glass-card rounded-xl p-5 flex items-center justify-between">
              <div className="flex items-center gap-4 min-w-0">
                {n.image_url && <img src={fileUrl(n.image_url)} alt="" className="w-12 h-12 rounded-lg object-cover border border-hairline shrink-0" />}
                <div className="min-w-0"><div className="text-ink truncate">{n.title}</div><div className="font-mono text-[11px] text-inkfaint mt-1">{n.category} · {n.date}</div></div>
              </div>
              <button onClick={() => del("news", n.id)} className="text-inkfaint hover:text-red-400 shrink-0 ml-3"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </TabsContent>

        {/* JUDGMENTS */}
        <TabsContent value="judgments" className="mt-8 space-y-6">
          <div className="glass-card rounded-2xl p-7 space-y-4">
            <h3 className="font-serif text-2xl text-ink flex items-center gap-2"><Plus className="w-5 h-5 text-gold" /> Upload Judgment</h3>
            <input className={inputCls} placeholder="Judgment headline / main tag line" value={leg.title} onChange={(e) => setLeg({ ...leg, title: e.target.value })} data-testid="legal-title" />
            <div className="grid sm:grid-cols-2 gap-4">
              <select className={inputCls} value={leg.doc_type} onChange={(e) => setLeg({ ...leg, doc_type: e.target.value })}>
                <option value="case_law">Case Law</option><option value="journal">Journal</option>
              </select>
              <select className={inputCls} value={leg.category} onChange={(e) => setLeg({ ...leg, category: e.target.value })}>
                <option>Supreme Court</option><option>High Court</option>
              </select>
              <input className={inputCls} placeholder="Citation" value={leg.citation} onChange={(e) => setLeg({ ...leg, citation: e.target.value })} />
              <input className={inputCls} placeholder="Date (YYYY-MM-DD)" value={leg.date} onChange={(e) => setLeg({ ...leg, date: e.target.value })} />
              <input className={inputCls} placeholder="Source" value={leg.source} onChange={(e) => setLeg({ ...leg, source: e.target.value })} />
            </div>
            <textarea className={inputCls} rows={3} placeholder="Description — facts & details of the judgment" value={leg.summary} onChange={(e) => setLeg({ ...leg, summary: e.target.value })} />
            <input className={inputCls} placeholder="Clickable link (https://… judgment / resource)" value={leg.link_url} onChange={(e) => setLeg({ ...leg, link_url: e.target.value })} data-testid="legal-link" />
            <div className="flex items-center gap-3 flex-wrap">
              <label className="rounded-full border border-hairline text-ink hover:bg-surface2 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider cursor-pointer flex items-center gap-2" data-testid="legal-pdf-label">
                {uploading === "leg" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {leg.attachment_name ? "Replace PDF" : "Attach PDF"}
                <input type="file" accept="application/pdf,.pdf" className="hidden" data-testid="legal-pdf-input" onChange={(e) => uploadPdf(e.target.files?.[0], "leg")} />
              </label>
              {leg.attachment_name && (
                <span className="font-mono text-[11px] text-inkmuted flex items-center gap-2">
                  {leg.attachment_name}
                  <button type="button" onClick={() => setLeg({ ...leg, attachment_url: "", attachment_name: "" })} className="text-inkfaint hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                </span>
              )}
            </div>
            <button onClick={addLegal} disabled={saving || !leg.title} className="rounded-full bg-btn text-btnink px-6 py-2.5 text-xs font-semibold uppercase tracking-wider disabled:opacity-50" data-testid="add-legal-btn">Add to Database</button>
          </div>
          {legal.map((d) => (
            <div key={d.id} className="glass-card rounded-xl p-5 flex items-center justify-between">
              <div><div className="text-ink">{d.title}</div><div className="font-mono text-[11px] text-inkfaint mt-1">{d.category} · {d.citation}</div></div>
              <button onClick={() => del("legal", d.id)} className="text-inkfaint hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </TabsContent>

        {/* VACANCIES */}
        <TabsContent value="vacancies" className="mt-8 space-y-6">
          <div className="glass-card rounded-2xl p-7 space-y-4">
            <h3 className="font-serif text-2xl text-ink flex items-center gap-2"><Plus className="w-5 h-5 text-gold" /> New Vacancy</h3>
            <input className={inputCls} placeholder="Title" value={vac.title} onChange={(e) => setVac({ ...vac, title: e.target.value })} data-testid="vac-title" />
            <div className="grid sm:grid-cols-2 gap-4">
              <input className={inputCls} placeholder="Organization" value={vac.organization} onChange={(e) => setVac({ ...vac, organization: e.target.value })} />
              <select className={inputCls} value={vac.type} onChange={(e) => setVac({ ...vac, type: e.target.value })}>
                <option value="internship">Internship</option><option value="clerkship">Clerkship</option><option value="associate">Associate</option><option value="government">Government</option><option value="judiciary">Judiciary</option><option value="exam">Exam</option>
              </select>
              <input className={inputCls} placeholder="Location" value={vac.location} onChange={(e) => setVac({ ...vac, location: e.target.value })} />
              <input className={inputCls} placeholder="Stipend (e.g. Rs. 15,000 / Month)" value={vac.stipend} onChange={(e) => setVac({ ...vac, stipend: e.target.value })} />
            </div>
            <input className={inputCls} placeholder="Tags (comma separated)" value={vac.tags} onChange={(e) => setVac({ ...vac, tags: e.target.value })} />
            <textarea className={inputCls} rows={3} placeholder="Description" value={vac.description} onChange={(e) => setVac({ ...vac, description: e.target.value })} />
            <input className={inputCls} placeholder="Clickable link (https://… official notification / apply link)" value={vac.link_url} onChange={(e) => setVac({ ...vac, link_url: e.target.value })} data-testid="vac-link" />
            <div className="flex items-center gap-3 flex-wrap">
              <label className="rounded-full border border-hairline text-ink hover:bg-surface2 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider cursor-pointer flex items-center gap-2" data-testid="vac-pdf-label">
                {uploading === "vac" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {vac.attachment_name ? "Replace PDF" : "Attach PDF"}
                <input type="file" accept="application/pdf,.pdf" className="hidden" data-testid="vac-pdf-input" onChange={(e) => uploadPdf(e.target.files?.[0], "vac")} />
              </label>
              {vac.attachment_name && (
                <span className="font-mono text-[11px] text-inkmuted flex items-center gap-2">
                  {vac.attachment_name}
                  <button type="button" onClick={() => setVac({ ...vac, attachment_url: "", attachment_name: "" })} className="text-inkfaint hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                </span>
              )}
            </div>
            <button onClick={addVac} disabled={saving || !vac.title} className="rounded-full bg-btn text-btnink px-6 py-2.5 text-xs font-semibold uppercase tracking-wider disabled:opacity-50" data-testid="add-vacancy-btn">Post Vacancy</button>
          </div>
          {vacancies.map((v) => (
            <div key={v.id} className="glass-card rounded-xl p-5 flex items-center justify-between">
              <div><div className="text-ink">{v.title}</div><div className="font-mono text-[11px] text-inkfaint mt-1">{v.organization} · {v.type}</div></div>
              <button onClick={() => del("vacancies", v.id)} className="text-inkfaint hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </TabsContent>

        {/* PYQs */}
        <TabsContent value="pyqs" className="mt-8 space-y-6">
          {/* AI generate */}
          <div className="glass-card rounded-2xl p-7 space-y-4" data-testid="pyq-gen-form">
            <h3 className="font-serif text-2xl text-ink flex items-center gap-2"><Plus className="w-5 h-5 text-gold" /> AI-Generate Practice Set</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <input className={inputCls} placeholder="Exam (e.g. Gujarat Judiciary)" value={gen.exam} onChange={(e) => setGen({ ...gen, exam: e.target.value })} data-testid="gen-exam" />
              <input className={inputCls} placeholder="Subject (e.g. Evidence Act)" value={gen.subject} onChange={(e) => setGen({ ...gen, subject: e.target.value })} data-testid="gen-subject" />
              <input className={inputCls} placeholder="Year (optional)" value={gen.year} onChange={(e) => setGen({ ...gen, year: e.target.value })} />
              <select className={inputCls} value={gen.qtype} onChange={(e) => setGen({ ...gen, qtype: e.target.value })}>
                <option value="mcq">MCQ</option><option value="descriptive">Descriptive</option>
              </select>
              <select className={inputCls} value={gen.count} onChange={(e) => setGen({ ...gen, count: e.target.value })}>
                {[3, 5, 10, 15, 20].map((n) => <option key={n} value={n}>{n} questions</option>)}
              </select>
            </div>
            <button onClick={genPyq} disabled={generating} className="rounded-full bg-gold text-black px-6 py-2.5 text-xs font-semibold uppercase tracking-wider disabled:opacity-50 flex items-center gap-2" data-testid="gen-pyq-btn">
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Generate with AI
            </button>
          </div>

          {/* Manual add */}
          <div className="glass-card rounded-2xl p-7 space-y-4" data-testid="pyq-form">
            <h3 className="font-serif text-2xl text-ink flex items-center gap-2"><Plus className="w-5 h-5 text-gold" /> Add Real PYQ</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <input className={inputCls} placeholder="Exam" value={pyq.exam} onChange={(e) => setPyq({ ...pyq, exam: e.target.value })} data-testid="pyq-exam" />
              <input className={inputCls} placeholder="Subject" value={pyq.subject} onChange={(e) => setPyq({ ...pyq, subject: e.target.value })} data-testid="pyq-subject" />
              <input className={inputCls} placeholder="Year (e.g. 2023)" value={pyq.year} onChange={(e) => setPyq({ ...pyq, year: e.target.value })} />
              <select className={inputCls} value={pyq.qtype} onChange={(e) => setPyq({ ...pyq, qtype: e.target.value })}>
                <option value="mcq">MCQ</option><option value="descriptive">Descriptive</option>
              </select>
            </div>
            <textarea className={inputCls} rows={2} placeholder="Question" value={pyq.question} onChange={(e) => setPyq({ ...pyq, question: e.target.value })} data-testid="pyq-question" />
            {pyq.qtype === "mcq" && (
              <div className="grid sm:grid-cols-2 gap-4">
                <input className={inputCls} placeholder="Option A" value={pyq.optA} onChange={(e) => setPyq({ ...pyq, optA: e.target.value })} />
                <input className={inputCls} placeholder="Option B" value={pyq.optB} onChange={(e) => setPyq({ ...pyq, optB: e.target.value })} />
                <input className={inputCls} placeholder="Option C" value={pyq.optC} onChange={(e) => setPyq({ ...pyq, optC: e.target.value })} />
                <input className={inputCls} placeholder="Option D" value={pyq.optD} onChange={(e) => setPyq({ ...pyq, optD: e.target.value })} />
              </div>
            )}
            <input className={inputCls} placeholder={pyq.qtype === "mcq" ? "Correct answer letter (A/B/C/D)" : "Model answer"} value={pyq.answer} onChange={(e) => setPyq({ ...pyq, answer: e.target.value })} data-testid="pyq-answer" />
            <textarea className={inputCls} rows={2} placeholder="Explanation (optional)" value={pyq.explanation} onChange={(e) => setPyq({ ...pyq, explanation: e.target.value })} />
            <button onClick={addPyq} disabled={saving || !pyq.question || !pyq.answer} className="rounded-full bg-btn text-btnink px-6 py-2.5 text-xs font-semibold uppercase tracking-wider disabled:opacity-50" data-testid="add-pyq-btn">Add Question</button>
          </div>

          <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-inkfaint">{pyqs.length} questions in bank</p>
          {pyqs.map((p) => (
            <div key={p.id} className="glass-card rounded-xl p-5 flex items-center justify-between" data-testid="admin-pyq-row">
              <div className="min-w-0">
                <div className="text-ink truncate">{p.question}</div>
                <div className="font-mono text-[11px] text-inkfaint mt-1">{p.exam} · {p.subject} · {p.year || "—"} · {p.qtype} · <span className="text-gold">{p.source}</span></div>
              </div>
              <button onClick={() => del("pyq", p.id)} className="text-inkfaint hover:text-red-400 shrink-0 ml-3"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </TabsContent>

        {/* APPLICATIONS */}
        <TabsContent value="applications" className="mt-8 space-y-3">
          {applications.length === 0 ? <p className="text-inkfaint font-mono text-sm">No applications yet.</p> :
            applications.map((a) => (
              <div key={a.id} className="glass-card rounded-xl p-5" data-testid="admin-application-row">
                <div className="text-ink">{a.name} — <span className="text-inkmuted">{a.vacancy_title}</span></div>
                <div className="font-mono text-[11px] text-inkfaint mt-1">{a.email} · {a.status}</div>
              </div>
            ))}
        </TabsContent>

        {/* VERIFICATIONS */}
        <TabsContent value="verifications" className="mt-8 space-y-3">
          {verifications.length === 0 ? <p className="text-inkfaint font-mono text-sm">No verifications submitted.</p> :
            verifications.map((vf) => (
              <div key={vf.id} className="glass-card rounded-xl p-5 flex items-center justify-between" data-testid="admin-verification-row">
                <div>
                  <div className="text-ink">{vf.email}<span className="font-mono text-[10px] uppercase tracking-[0.15em] text-gold ml-2">{vf.type}</span></div>
                  <div className="font-mono text-[11px] text-inkfaint mt-1">{vf.type === "student" ? vf.college_name : vf.bar_council_id} · {vf.status}</div>
                </div>
              </div>
            ))}
        </TabsContent>

        {/* USERS */}
        <TabsContent value="users" className="mt-8 space-y-3">          {users.map((u) => (
            <div key={u.user_id} className="glass-card rounded-xl p-5 flex items-center justify-between" data-testid="admin-user-row">
              <div><div className="text-ink">{u.name}</div><div className="font-mono text-[11px] text-inkfaint mt-1">{u.email}</div></div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-gold">{u.plan}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-inkfaint">{u.role}</span>
              </div>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
